import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/axios/api'

import { AxiosErrorHandler } from '@models/helpers/axiosErrorHandler'
import {
  type LoginRequestPayloadDataType,
  type LoginMFARequestPayloadDataType
} from '@models/requests/payloads'

import { isAvailableSessionStorage } from '@scripts/helpers/windowHelpers'
import { AppCookies } from '@models/helpers/cookies'

export const useAuthorizationStore = defineStore('useAuthorizationStore', () => {
  const isAuthorized = ref(false)
  const isCheckedAuthorization = ref(false)
  const emailForRestorePassword = ref('')
  const routeNameForAuthorizedStatusSwitching = ref<string | null>(null)
  const mfaToken = ref('')
  const mfaTokenExpiredError = ref(false)

  const setAuthorizationStatus = (status: boolean = true, routeName?: string | null) => {
    routeNameForAuthorizedStatusSwitching.value = routeName || (status ? null : 'login')
    isAuthorized.value = status
  }

  const setCheckedAuthorizationStatus = (status: boolean = true) => {
    isCheckedAuthorization.value = status
  }

  const setEmailForRestorePassword = (email: string) => {
    emailForRestorePassword.value = email
  }

  const setMFAToken = (token: string) => {
    mfaToken.value = token

    if (isAvailableSessionStorage()) {
      if (!!token) {
        sessionStorage.setItem('mfa_token', token)
      } else if (sessionStorage.getItem('mfa_token')) {
        sessionStorage.removeItem('mfa_token')
      }
    }
  }

  const setMFATokenExpiredError = (status: boolean = true) => {
    mfaTokenExpiredError.value = status
  }

  const login = async (payloadData: LoginRequestPayloadDataType | LoginMFARequestPayloadDataType) =>
    await api.post('/personal-api/v1/user/login/', payloadData)

  const logout = async (successRouteName: string = 'login') => {
    if (import.meta.env.MODE === 'development') {
      setAuthorizationStatus(false, successRouteName)
    } else {
      await api.post('/personal-api/v1/user/logout/')
        .then(() => {
          setAuthorizationStatus(false, successRouteName)
        })
        .catch((error) => {
          const errorHandler = new AxiosErrorHandler(error)
          if (errorHandler.isLogoutError) {
            setAuthorizationStatus(false)
          }
        })
    }
  }

  const sendEmailForResetPassword = async (email: string) =>
    await api.post('/personal-api/v1/user/request-password-reset/', { email })

  const resetPassword = async (newPassword: string, token?: string) => {
    const cookies = new AppCookies()

    return !!token
      ? await api.post('/personal-api/v1/user/password-reset/', {
        new_password: newPassword,
        token
      })
      : await api.post('/personal-api/v1/user/password-reset/', { new_password: newPassword }, {
        headers: {
          // @ts-ignore
          common: {
            'x-csrftoken': cookies.getCookie('csrftoken')
          }
        }
      })
  }


  return {
    isAuthorized,
    isCheckedAuthorization,
    routeNameForAuthorizedStatusSwitching,
    mfaToken,
    mfaTokenExpiredError,
    setAuthorizationStatus,
    setCheckedAuthorizationStatus,
    setEmailForRestorePassword,
    setMFAToken,
    setMFATokenExpiredError,
    login,
    logout,
    sendEmailForResetPassword,
    resetPassword
  }
})
